/**
 * MarginFields.jsx — the sheet's four edge margins, uniform or per side.
 *
 * Shared by Auto Imposer and the Layout Editor so the two cannot drift into
 * meaning different things by the same word.
 *
 * ─── WHY PER SIDE ──────────────────────────────────────────────────────────
 * The four edges are not interchangeable, and the backend has always taken them
 * separately (`margin_bottom` / `_top` / `_left` / `_right`, each validated on
 * its own). Only the UI insisted they be equal, which forced an operator who
 * needed a 12 mm gripper to give up 12 mm on the tail and both guides as well —
 * on a 711 × 508 sheet that is roughly 5% of the usable area thrown away to a
 * limitation of a form field.
 *
 * ─── WHY THE FIELDS ARE LAID OUT LIKE A SHEET ──────────────────────────────
 * Top above, left and right flanking, bottom below. An operator setting a
 * gripper is thinking about an edge of a physical sheet, not the third row of a
 * list, and a stack of four identical inputs labelled top/right/bottom/left is
 * exactly where a transposed pair goes unnoticed.
 *
 * ─── THE NAMES ─────────────────────────────────────────────────────────────
 * Margin keys map to MNE's Y-up sheet edges (contract v3.0): bottom is the
 * press GRIPPER (leading edge), top is the TAIL, left and right are the GUIDES.
 * Those are the words on the press, so they are the words here, with the
 * geometric name kept alongside because the payload uses it.
 */

import React from 'react';
import LenInput from './LenInput';
import { useTheme } from './theme';
import { ZERO_MARGINS as ZERO } from './editorDoc';

/** Edge → what the press calls it. Order is the visual one, not the payload's. */
export const EDGE_LABELS = {
  top: 'Tail',
  left: 'Guide',
  right: 'Guide',
  bottom: 'Gripper',
};

// The margin RULES live in editorDoc.js, where node can reach them: which
// number a collapse picks is a press-safety decision and belongs under test,
// not inside a component.
export { ZERO_MARGINS, marginsAreUniform, collapseToUniform } from './editorDoc';

export default function MarginFields({
  mode, onMode, uniform, sides, onUniform, onSides, error = null, disabled = false,
}) {
  const { P, MO, FN, inp, unit } = useTheme();
  const s = sides || ZERO;

  const tab = (on) => ({
    flex: 1, fontFamily: MO, fontSize: 10.5, padding: '5px 8px', borderRadius: 5,
    cursor: disabled ? 'default' : 'pointer',
    background: on ? P.ink : 'transparent', color: on ? P.sheet : P.muted,
    border: `0.5px solid ${on ? P.ink : P.rule}`,
  });

  const field = (edge, width = '100%') => (
    <label style={{ display: 'block', width, fontFamily: MO, fontSize: 9.5, color: P.dim }}>
      <span style={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
        {EDGE_LABELS[edge]}
      </span>
      <LenInput
        mm={Number(s[edge]) || 0}
        onMm={(v) => onSides({ ...s, [edge]: Math.max(0, v) })}
        stepMm={1}
        title={`${EDGE_LABELS[edge]} — the sheet's ${edge} edge (margins.${edge})`}
        style={{ ...inp, marginTop: 0, padding: '5px 8px', fontSize: 12 }}
      />
    </label>
  );

  return (
    <div style={{ opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        <button type="button" style={tab(mode === 'uniform')}
          onClick={() => onMode('uniform')}>Uniform</button>
        <button type="button" style={tab(mode === 'perSide')}
          onClick={() => onMode('perSide')}>Per side</button>
      </div>

      {mode === 'uniform' ? (
        <label style={{ display: 'block', fontFamily: MO, fontSize: 10.5, color: P.muted }}>
          All edges ({unit})
          <LenInput mm={Number(uniform) || 0} onMm={(v) => onUniform(Math.max(0, v))}
            stepMm={1} style={inp} />
        </label>
      ) : (
        // Laid out as the sheet itself, so an edge is picked by position and not
        // by reading a label off a list.
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div style={{ width: '58%' }}>{field('top')}</div>
          <div style={{ display: 'flex', gap: 6, width: '100%' }}>
            {field('left', '50%')}
            {field('right', '50%')}
          </div>
          <div style={{ width: '58%' }}>{field('bottom')}</div>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, fontFamily: MO, fontSize: 10.5, color: P.danger, lineHeight: 1.4 }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 8, lineHeight: 1.45 }}>
        Reserved strips no die may enter. <strong>Gripper</strong> is the press's leading
        edge (bottom); <strong>tail</strong> is opposite it; the <strong>guides</strong> are
        the sides. Only the edges you set cost you sheet.
      </div>
    </div>
  );
}
